import { useRef, useState } from "react";
import { apiClient } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileText, Download, Trash2, FolderPlus, Folder, Files, X, Eye, ArrowLeftRight, Lock, Users } from "lucide-react";
import { formatBytes, formatDate, DD_SIDES, sideLabel, otherSide } from "@/lib/dataroom";

function isPreviewable(doc) {
  const ct = (doc.content_type || "").toLowerCase();
  const n = (doc.original_filename || "").toLowerCase();
  return ct === "application/pdf" || n.endsWith(".pdf") || ct.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/.test(n);
}

function SideToggle({ side, setSide, lockedSide, counts }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid="docs-side-toggle">
      {DD_SIDES.map((s) => {
        const mine = lockedSide && lockedSide === s.key;
        const theirs = lockedSide && lockedSide !== s.key;
        return (
          <button key={s.key} onClick={() => setSide(s.key)} data-testid={`docs-side-${s.key}`} data-active={side === s.key}
            className={cn("px-4 h-9 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-1.5",
              side === s.key ? "bg-brand-ink text-white" : "text-slate-500 hover:text-brand-ink")}>
            {mine && <Lock className="w-3.5 h-3.5 text-brand-lime" />}
            {theirs && <Users className="w-3.5 h-3.5 opacity-70" />}
            {s.label}
            {mine && <span className="opacity-70 font-normal">· din side</span>}
            {theirs && <span className="opacity-70 font-normal">· motpart</span>}
            <span className="opacity-70 font-normal">({counts?.[s.key] ?? 0})</span>
          </button>
        );
      })}
    </div>
  );
}

function SideBanner({ side, lockedSide }) {
  const label = sideLabel(side);
  const readOnly = lockedSide && lockedSide !== side;
  return (
    <div className={cn("rounded-2xl border px-5 py-3 flex items-center gap-3",
      readOnly ? "bg-slate-50 border-slate-200" : "bg-brand-limeSoft/60 border-brand-lime/40")} data-testid="docs-side-banner">
      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", readOnly ? "bg-white text-slate-500" : "bg-brand-ink text-brand-lime")}>
        {readOnly ? <Users className="w-4 h-4" /> : <Files className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-brand-ink text-sm">
          {readOnly ? `Dokumenter fra motpart (${label})` : `${label}s dokumenter`}
        </p>
        <p className="text-xs text-slate-500">
          {readOnly
            ? "Dette er lastet opp av den andre parten. Du kan se og laste ned, men ikke endre."
            : lockedSide
              ? "Alt du laster opp her blir synlig for motparten i datarommet."
              : `Dokumenter og mapper som tilhører ${label.toLowerCase()}siden. Motparten ser dette under sin fane «${label}».`}
        </p>
      </div>
    </div>
  );
}

export default function DocumentsTab({ deal, canManage }) {
  const qc = useQueryClient();
  const dealId = deal.id;
  const fileRef = useRef(null);
  const lockedSide = (deal.my_external && (deal.my_party === "kjoper" || deal.my_party === "selger")) ? deal.my_party : null;
  const [side, setSide] = useState(lockedSide || (deal.my_party === "selger" ? "selger" : "kjoper"));
  const [activeFolder, setActiveFolder] = useState(null); // null = all
  const [newFolder, setNewFolder] = useState("");
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [preview, setPreview] = useState(null); // { url, type, name, loading }

  const canWrite = canManage && (!lockedSide || lockedSide === side);
  const canMove = canManage && !lockedSide;

  const switchSide = (s) => { setSide(s); setActiveFolder(null); setShowAddFolder(false); };

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["deal-docs", dealId, side, activeFolder],
    queryFn: async () => {
      const params = { side, ...(activeFolder ? { folder_id: activeFolder } : {}) };
      return (await apiClient.get(`/deals/${dealId}/documents`, { params })).data;
    },
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["deal-docs", dealId] });
    qc.invalidateQueries({ queryKey: ["deal", dealId] });
    qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
  };

  const uploadMutation = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("side", side);
      if (activeFolder) fd.append("folder_id", activeFolder);
      return apiClient.post(`/deals/${dealId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: () => { invalidateAll(); toast.success(`Dokument lastet opp til ${sideLabel(side)}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Opplasting feilet"),
  });

  const addFolderMutation = useMutation({
    mutationFn: (name) => apiClient.post(`/deals/${dealId}/folders`, { name, side }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      setNewFolder(""); setShowAddFolder(false);
      toast.success("Mappe opprettet");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke opprette mappe"),
  });

  const deleteMutation = useMutation({
    mutationFn: (docId) => apiClient.delete(`/deals/${dealId}/documents/${docId}`),
    onSuccess: () => { invalidateAll(); setDeleteDoc(null); toast.success("Dokument slettet"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke slette"),
  });

  const moveMutation = useMutation({
    mutationFn: (doc) => apiClient.put(`/deals/${dealId}/documents/${doc.id}`, { side: otherSide(doc.side || "kjoper") }),
    onSuccess: (res) => { invalidateAll(); toast.success(`Flyttet til ${sideLabel(res.data.side)}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke flytte"),
  });

  const canDownload = deal.my_permission === "manage" || deal.my_permission === "download";

  const handleDownload = async (doc) => {
    try {
      const res = await apiClient.get(`/deals/${dealId}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "fil";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
    } catch {
      toast.error("Nedlasting feilet");
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = "";
  };

  const openPreview = async (doc) => {
    setPreview({ loading: true, name: doc.original_filename });
    try {
      const res = await apiClient.get(`/deals/${dealId}/documents/${doc.id}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ct = (doc.content_type || "").toLowerCase();
      const isImg = ct.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/.test((doc.original_filename || "").toLowerCase());
      setPreview({ url, type: isImg ? "image" : "pdf", name: doc.original_filename });
      qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
    } catch {
      setPreview(null);
      toast.error("Kunne ikke åpne forhåndsvisning");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const folders = (deal.folders || []).filter((f) => (f.side || "kjoper") === side);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <SideToggle side={side} setSide={switchSide} lockedSide={lockedSide} counts={deal.document_counts} />
        {canWrite && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={onFileChange} data-testid="file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploadMutation.isPending} data-testid="upload-button">
              <Upload className="w-4 h-4 mr-2" /> {uploadMutation.isPending ? "Laster opp..." : `Last opp til ${sideLabel(side)}`}
            </Button>
          </div>
        )}
      </div>

      <SideBanner side={side} lockedSide={lockedSide} />

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Folder sidebar (per side) */}
        <div className="space-y-1" data-testid={`docs-folders-${side}`}>
          <button
            onClick={() => setActiveFolder(null)}
            data-testid="folder-all"
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
              activeFolder === null ? "bg-brand-limeSoft text-brand-ink font-semibold" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Files className="w-4 h-4" /> Alle dokumenter
          </button>
          {folders.map((f) => (
            <div key={f.id} className="group flex items-center">
              <button
                onClick={() => setActiveFolder(f.id)}
                data-testid={`folder-${f.id}`}
                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  activeFolder === f.id ? "bg-brand-limeSoft text-brand-ink font-semibold" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Folder className="w-4 h-4 shrink-0" /> <span className="truncate">{f.name}</span>
              </button>
            </div>
          ))}
          {canWrite && (
            <div className="pt-2">
              {showAddFolder ? (
                <div className="flex items-center gap-1">
                  <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="Mappenavn" className="h-9 text-sm" data-testid="new-folder-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && newFolder.trim()) addFolderMutation.mutate(newFolder.trim()); }} />
                  <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => { setShowAddFolder(false); setNewFolder(""); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" className="w-full justify-start text-slate-500" onClick={() => setShowAddFolder(true)} data-testid="add-folder-button">
                  <FolderPlus className="w-4 h-4 mr-2" /> Ny mappe
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div>
          <p className="text-sm text-slate-500 mb-3" data-testid="docs-count">{docs.length} dokument{docs.length === 1 ? "" : "er"} · {sideLabel(side)}</p>

          {isLoading ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-white rounded-xl animate-pulse" />)}</div>
          ) : docs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200/70" data-testid="docs-empty">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Ingen dokumenter fra {sideLabel(side).toLowerCase()} her ennå</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map((doc) => (
                <div key={doc.id} data-testid={`doc-row-${doc.id}`} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200/70 px-4 py-3 hover:shadow-sm transition-shadow">
                  <div className="w-9 h-9 rounded-lg bg-brand-limeSoft text-brand-limeDark flex items-center justify-center shrink-0">
                    <FileText className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-brand-ink text-sm truncate">{doc.original_filename}</p>
                    <p className="text-xs text-slate-400">{formatBytes(doc.size)} · {doc.uploaded_by_name || doc.uploaded_by} · {formatDate(doc.created_at)}</p>
                  </div>
                  {isPreviewable(doc) && (
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-brand-ink" onClick={() => openPreview(doc)} data-testid={`preview-${doc.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  {canDownload && (
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-brand-ink" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  {canMove && (
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-brand-ink" title={`Flytt til ${sideLabel(otherSide(side))}`}
                      onClick={() => moveMutation.mutate(doc)} disabled={moveMutation.isPending} data-testid={`move-doc-${doc.id}`}>
                      <ArrowLeftRight className="w-4 h-4" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" onClick={() => setDeleteDoc(doc)} data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteDoc} onOpenChange={(v) => !v && setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette dokument?</AlertDialogTitle>
            <AlertDialogDescription>«{deleteDoc?.original_filename}» fjernes fra datarommet.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate(deleteDoc.id)} data-testid="confirm-delete-doc">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[88vh] flex flex-col p-0 gap-0" data-testid="preview-dialog">
          <DialogHeader className="px-5 py-3 border-b border-slate-100">
            <DialogTitle className="text-brand-ink text-base truncate pr-8">{preview?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 flex items-center justify-center overflow-auto">
            {preview?.loading ? (
              <p className="text-slate-500 text-sm">Laster forhåndsvisning...</p>
            ) : preview?.type === "image" ? (
              <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" data-testid="preview-image" />
            ) : preview?.url ? (
              <iframe src={preview.url} title={preview.name} className="w-full h-full" data-testid="preview-frame" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
